import { useState, useMemo, useEffect, useCallback, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { GapFilterBanner } from '@/components/repGaps/GapFilterBanner'
import { repGapFor } from '@/lib/repGaps'
import {
  useAccounts,
  useDeleteAccount,
  useBulkDeleteAccounts,
  useBulkUpdateAccounts,
  useBulkAssignAccounts,
  useUsers,
} from '@/hooks/use-api'
import { ownerPickerOptions } from '@/lib/salesReps'
import { usePagination } from '@/hooks/use-pagination'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { RefreshButton } from '@/components/RefreshButton'
import { SearchWithRecent } from '@/components/ui/search-with-recent'
import { ResizableTable } from '@/components/ui/resizable-table'
import { CategoryBadge } from '@/components/ui/CategoryBadge'
import { Building2, Plus, Users, Download } from 'lucide-react'
import type { Account as _Account } from '@/types'
import { exportAccountsToCSV } from '@/lib/csv-export'
import { CreateAccountModal } from '@/components/CreateAccountModal'
import { BulkActionsBar } from '@/components/BulkActionsBar'
import { BulkDeleteDialog } from '@/components/BulkDeleteDialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { BulkUpdateModal, type BulkUpdateField } from '@/components/BulkUpdateModal'
import { BulkAssignModal } from '@/components/BulkAssignModal'
import { MergeRecordsModal } from '@/components/MergeRecordsModal'
import {
  createAccountsColumns,
  accountsDefaultColumnSizing,
} from '@/config/columns/accounts-columns'
import { ColumnPicker } from '@/components/ui/column-picker'
import { useColumnVisibility } from '@/hooks/use-column-visibility'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useUser } from '@/contexts/UserContext'
import { usePersistentState, makeFilterKey } from '@/hooks/use-persistent-state'
import { useToast } from '@/hooks/use-toast'
import { useReadOnly } from '@/hooks/use-read-only'
import { MobileAccountList } from '@/components/MobileAccountCard'
import { explainError } from '@/lib/explain-error'

export default function Accounts() {
  const navigate = useNavigate()
  const { currentUser, isAdmin, impersonatedUser } = useUser()
  const fk = makeFilterKey(currentUser?.id, 'accounts')
  const [search, setSearch] = useState('')
  const { page, firstPage, prevPage, nextPage, extract } = usePagination()
  const [showMyAccounts, setShowMyAccounts] = usePersistentState(fk('showMy'), !isAdmin)
  const [sortBy, setSortBy] = usePersistentState(fk('sortBy'), 'name')
  const [sortOrder, setSortOrder] = usePersistentState<'asc' | 'desc'>(fk('sortOrder'), 'asc')

  // Owner scope from a dashboard deep-link (?owner_id=… / ?my). Overrides the
  // My/All toggle on arrival so the list matches the card; cleared on toggle.
  const [searchParams] = useSearchParams()
  const [deepLinkOwnerId, setDeepLinkOwnerId] = useState<string | undefined>(undefined)
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [accountToDelete, setAccountToDelete] = useState<{ id: string; name: string } | null>(null)
  const [bulkUpdateModalOpen, setBulkUpdateModalOpen] = useState(false)
  const [bulkAssignModalOpen, setBulkAssignModalOpen] = useState(false)
  const [mergeModalOpen, setMergeModalOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(false)

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768) // md breakpoint
    }

    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Only Admin/Super Admin can export (and not when impersonating a non-admin)
  const canExport =
    isAdmin && (!impersonatedUser || ['Admin', 'Super Admin'].includes(impersonatedUser.user_type))
  // Only Admin/Super Admin can delete (single or bulk) — for cleanup
  const canDelete =
    isAdmin && (!impersonatedUser || ['Admin', 'Super Admin'].includes(impersonatedUser.user_type))
  // Only Admin/Super Admin can bulk-reassign the owner
  const canReassignOwner = canDelete
  const [resellersOnly, setResellersOnly] = usePersistentState(fk('resellersOnly'), false)
  // Follow-up filters (same rules as the dashboard; backend dealActivity.js). The stored
  // values keep their old names so remembered choices still work: no_tasks = nothing scheduled,
  // no_contact_90 = no contact in 90+ days.
  const [activeFilter, setActiveFilter] = usePersistentState<
    '' | 'no_tasks' | 'no_contact_90' | 'stale_no_next'
  >(fk('activeFilter'), '')
  // Stale filter (no activity in N+ days) from a Daily Summary email deep-link
  // (?last_activity_days=60). Not persisted — it's a per-visit, link-scoped view.
  const [staleDays, setStaleDays] = useState<number | undefined>(undefined)
  // Email-only filter (?engagement=email_only) from the Exec Sales Dashboard / Daily Summary.
  // Link-scoped like staleDays; there is no on-page control for it.
  const [emailOnly, setEmailOnly] = useState(false)
  // Exec Sales Dashboard "Follow-through" drill-down (?gap=renewal_unworked).
  const repGap = repGapFor('account', searchParams.get('gap'))

  // Apply the dashboard deep-link once: scope to the card's owner and clear any
  // leftover ad-hoc filters so the list matches the card's count.
  const dashboardAppliedRef = useRef(false)
  useEffect(() => {
    if (dashboardAppliedRef.current) return
    if (
      !searchParams.has('owner_id') &&
      !searchParams.has('my') &&
      !searchParams.has('last_activity_days') &&
      !searchParams.has('engagement') &&
      !searchParams.has('gap') &&
      !searchParams.has('followup')
    )
      return
    dashboardAppliedRef.current = true
    setResellersOnly(false)
    // ?followup= from the dashboard's follow-up cards sets the matching button.
    const fu = searchParams.get('followup')
    setActiveFilter(
      fu === 'no_next_step'
        ? 'no_tasks'
        : fu === 'stale'
          ? 'no_contact_90'
          : fu === 'stale_no_next'
            ? 'stale_no_next'
            : ''
    )
    const me = impersonatedUser?.id || currentUser?.id
    const ownerId = searchParams.get('owner_id') || (searchParams.has('my') ? me : undefined)
    setDeepLinkOwnerId(ownerId)
    setShowMyAccounts(!!ownerId && ownerId === me)
    // Stale window from the Daily Summary "View all" link.
    const lad = parseInt(searchParams.get('last_activity_days') || '', 10)
    if (Number.isFinite(lad) && lad > 0) setStaleDays(lad)
    if (searchParams.get('engagement') === 'email_only') setEmailOnly(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { data, isLoading } = useAccounts({
    search,
    page,
    owner_id:
      deepLinkOwnerId ?? (showMyAccounts ? impersonatedUser?.id || currentUser?.id : undefined),
    account_type: resellersOnly ? 'Reseller' : undefined,
    followup:
      activeFilter === 'no_tasks'
        ? 'no_next_step'
        : activeFilter === 'no_contact_90'
          ? 'stale'
          : activeFilter === 'stale_no_next'
            ? 'stale_no_next'
            : undefined,
    last_activity_days: staleDays ? String(staleDays) : undefined,
    engagement: emailOnly ? 'email_only' : undefined,
    gap: repGap,
    sort_by: sortBy,
    sort_order: sortOrder,
  } as any)
  const bulkDeleteMutation = useBulkDeleteAccounts()
  const deleteMutation = useDeleteAccount()
  const bulkUpdateMutation = useBulkUpdateAccounts()
  const { data: usersData } = useUsers({ limit: 10000 })
  const users = usersData?.data ?? []
  const bulkAssignMutation = useBulkAssignAccounts()
  const { toast } = useToast()
  const isReadOnly = useReadOnly()

  const {
    items: accounts,
    total,
    currentPage,
    limit,
    totalPages,
    hasNextPage,
    hasPrevPage,
    stats,
  } = extract(data)

  const handleSort = useCallback(
    (field: string) => {
      if (sortBy === field) {
        setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))
      } else {
        setSortBy(field)
        setSortOrder('asc')
      }
      firstPage()
    },
    [sortBy, firstPage]
  )

  // Handle selection
  const handleToggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }

  const handleToggleSelectAll = () => {
    if (selectedIds.size === accounts.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(accounts.map((account) => account.id)))
    }
  }

  const handleClearSelection = () => {
    setSelectedIds(new Set())
  }

  // Handle bulk delete
  const handleBulkDelete = async () => {
    try {
      const idsArray = Array.from(selectedIds)
      await bulkDeleteMutation.mutateAsync(idsArray)

      toast({
        title: 'Success',
        description: `${idsArray.length} account${idsArray.length === 1 ? '' : 's'} deleted successfully.`,
      })

      setBulkDeleteDialogOpen(false)
      setSelectedIds(new Set())
    } catch (error) {
      const errorMessage = explainError(error, 'Failed to delete accounts')
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    }
  }

  // Handle bulk update
  const handleBulkUpdate = async (updates: Record<string, any>) => {
    try {
      await bulkUpdateMutation.mutateAsync({ ids: Array.from(selectedIds), updates })

      toast({
        title: 'Success',
        description: `${selectedIds.size} account${selectedIds.size === 1 ? '' : 's'} updated successfully.`,
      })

      setBulkUpdateModalOpen(false)
      setSelectedIds(new Set())
    } catch (error) {
      const errorMessage = explainError(error, 'Failed to update accounts')
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    }
  }

  // Handle bulk assign
  const handleBulkAssign = async (ownerId: string, notifyOwner: boolean) => {
    try {
      await bulkAssignMutation.mutateAsync({
        ids: Array.from(selectedIds),
        owner_id: ownerId,
        notify_owner: notifyOwner,
      })

      toast({
        title: 'Success',
        description: `${selectedIds.size} account${selectedIds.size === 1 ? '' : 's'} assigned successfully.`,
      })

      setBulkAssignModalOpen(false)
      setSelectedIds(new Set())
    } catch (error) {
      const errorMessage = explainError(error, 'Failed to assign accounts')
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    }
  }

  // Handle CSV export
  const handleExportCSV = () => {
    // For now, export the current page of accounts
    // In a production app, you might want to fetch all accounts from the API
    exportAccountsToCSV(accounts)
    toast({
      title: 'Export Successful',
      description: `Exported ${accounts.length} accounts to CSV`,
    })
  }

  // Define field configurations
  const accountsUpdateFields: BulkUpdateField[] = [
    {
      key: 'type',
      label: 'Type',
      type: 'select',
      options: [
        { value: 'Customer', label: 'Customer' },
        { value: 'Prospect', label: 'Prospect' },
        { value: 'Partner', label: 'Partner' },
      ],
    },
    { key: 'industry', label: 'Industry', type: 'text', placeholder: 'Enter industry' },
    {
      key: 'rating',
      label: 'Rating',
      type: 'select',
      options: [
        { value: 'Hot', label: 'Hot' },
        { value: 'Warm', label: 'Warm' },
        { value: 'Cold', label: 'Cold' },
      ],
    },
    {
      key: 'account_source',
      label: 'Account Source',
      type: 'text',
      placeholder: 'Enter account source',
    },
    ...(canReassignOwner
      ? [
          {
            key: 'owner_id',
            label: 'Owner',
            type: 'select' as const,
            options: ownerPickerOptions(users).map((u) => ({ value: u.id, label: u.name })),
          },
        ]
      : []),
  ]

  // Handle single delete
  const handleDeleteConfirm = async () => {
    if (!accountToDelete) return
    try {
      await deleteMutation.mutateAsync(accountToDelete.id)
      toast({
        title: 'Success',
        description: `Account "${accountToDelete.name}" deleted successfully.`,
      })
      setDeleteDialogOpen(false)
      setAccountToDelete(null)
    } catch (error) {
      const errorMessage = explainError(error, 'Failed to delete account')
      toast({ title: 'Error', description: errorMessage, variant: 'destructive' })
    }
  }

  // Create columns
  const columns = useMemo(
    () =>
      createAccountsColumns({
        selectedIds,
        onToggleSelect: handleToggleSelect,
        onToggleSelectAll: handleToggleSelectAll,
        allSelected: selectedIds.size === accounts.length && accounts.length > 0,
        onDeleteAccount: canDelete
          ? (id: string, name: string) => {
              setAccountToDelete({ id, name })
              setDeleteDialogOpen(true)
            }
          : undefined,
      }),
    [selectedIds, accounts.length, canDelete]
  )

  const [columnVisibility, setColumnVisibility] = useColumnVisibility('accounts-column-visibility')

  return (
    <div className="space-y-4">
      {selectedIds.size > 0 && (
        <BulkActionsBar
          selectedCount={selectedIds.size}
          entityType="account"
          onClearSelection={handleClearSelection}
          onBulkDelete={canDelete ? () => setBulkDeleteDialogOpen(true) : undefined}
          onBulkUpdate={() => setBulkUpdateModalOpen(true)}
          onBulkAssign={() => setBulkAssignModalOpen(true)}
          onMerge={() => setMergeModalOpen(true)}
          isDeleting={bulkDeleteMutation.isPending}
          isUpdating={bulkUpdateMutation.isPending}
          isAssigning={bulkAssignMutation.isPending}
        />
      )}

      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <CategoryBadge type="account" />
            <h1 className="text-3xl font-bold tracking-tight">Accounts</h1>
          </div>
          <p className="text-muted-foreground mt-1">
            Manage your company accounts and relationships
          </p>
          <GapFilterBanner
            gap={repGap}
            clearTo="/accounts"
            ownerName={
              deepLinkOwnerId
                ? (users.find((u) => u.id === deepLinkOwnerId)?.name ?? 'selected rep')
                : undefined
            }
          />
        </div>
        <div className="flex gap-2">
          <RefreshButton queryKey={['accounts']} label="accounts" />
          {canExport && (
            <Button variant="outline" aria-label="Export accounts to CSV" onClick={handleExportCSV}>
              <Download className="mr-2 h-4 w-4" aria-hidden="true" />
              Export
            </Button>
          )}
          <Button
            aria-label="Create new account"
            onClick={() => setCreateModalOpen(true)}
            disabled={isReadOnly}
            title={isReadOnly ? 'Cannot create accounts while viewing as another user' : undefined}
          >
            <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
            New Account
          </Button>
        </div>
      </div>

      <div className="grid gap-2 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
            <CardTitle className="text-sm font-medium">Total Accounts</CardTitle>
            <Building2 className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          </CardHeader>
          <CardContent className="p-3">
            <div className="text-xl font-bold leading-none">{total}</div>
            <p className="text-xs text-muted-foreground leading-tight">
              Showing {accounts.length} on this page
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
            <CardTitle className="text-sm font-medium">Active Accounts</CardTitle>
            <Users className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          </CardHeader>
          <CardContent className="p-3">
            <div className="text-xl font-bold leading-none">{stats?.activeAccounts ?? 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
            <CardTitle className="text-sm font-medium">Industries</CardTitle>
            <Building2 className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          </CardHeader>
          <CardContent className="p-3">
            <div className="text-xl font-bold leading-none">{stats?.industries ?? 0}</div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{showMyAccounts ? 'My Accounts' : 'All Accounts'}</CardTitle>
          <CardDescription>View and manage your company accounts</CardDescription>
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
            <Tabs
              defaultValue="all"
              value={showMyAccounts ? 'my' : 'all'}
              onValueChange={(value) => {
                setDeepLinkOwnerId(undefined)
                setShowMyAccounts(value === 'my')
                firstPage()
              }}
            >
              <TabsList>
                <TabsTrigger value="all" aria-label="Show all accounts">
                  All Accounts
                </TabsTrigger>
                <TabsTrigger value="my" aria-label="Show only my accounts">
                  My Accounts
                </TabsTrigger>
              </TabsList>
            </Tabs>
            <SearchWithRecent
              value={search}
              onChange={setSearch}
              placeholder="Search accounts..."
              ariaLabel="Search accounts"
              entityKey="accounts"
            />
            <Button
              variant={resellersOnly ? 'default' : 'outline'}
              size="sm"
              onClick={() => {
                setResellersOnly((prev) => !prev)
                setActiveFilter('')
                firstPage()
              }}
              aria-pressed={resellersOnly}
              aria-label={resellersOnly ? 'Show all accounts' : 'Show resellers only'}
            >
              Resellers
            </Button>
            <Button
              variant={activeFilter === 'no_tasks' ? 'default' : 'outline'}
              size="sm"
              onClick={() => {
                setActiveFilter((prev) => (prev === 'no_tasks' ? '' : 'no_tasks'))
                setResellersOnly(false)
                firstPage()
              }}
              aria-pressed={activeFilter === 'no_tasks'}
              aria-label="Show accounts with nothing scheduled"
              title="No open task (or one more than 30 days overdue) and no upcoming meeting"
            >
              Nothing Scheduled
            </Button>
            <Button
              variant={activeFilter === 'no_contact_90' ? 'default' : 'outline'}
              size="sm"
              onClick={() => {
                setActiveFilter((prev) => (prev === 'no_contact_90' ? '' : 'no_contact_90'))
                setResellersOnly(false)
                firstPage()
              }}
              aria-pressed={activeFilter === 'no_contact_90'}
              aria-label="Show accounts with no contact in 90+ days"
            >
              No Contact 90d
            </Button>
            <Button
              variant={activeFilter === 'stale_no_next' ? 'default' : 'outline'}
              size="sm"
              onClick={() => {
                setActiveFilter((prev) => (prev === 'stale_no_next' ? '' : 'stale_no_next'))
                setResellersOnly(false)
                firstPage()
              }}
              aria-pressed={activeFilter === 'stale_no_next'}
              aria-label="Show accounts with no contact in 90+ days and nothing scheduled"
            >
              Stale, Nothing Scheduled
            </Button>
            {staleDays ? (
              <Button
                variant="default"
                size="sm"
                onClick={() => {
                  setStaleDays(undefined)
                  firstPage()
                }}
                aria-label={`Clear filter: no activity in ${staleDays}+ days`}
              >
                No Activity {staleDays}d+ ✕
              </Button>
            ) : null}
            {emailOnly ? (
              <Button
                variant="default"
                size="sm"
                onClick={() => {
                  setEmailOnly(false)
                  firstPage()
                }}
                aria-label="Clear filter: email-only accounts"
                title="Only the odd email in the last 6 months, no calls, meetings or completed tasks"
              >
                Email Only ✕
              </Button>
            ) : null}
            {deepLinkOwnerId ? (
              <Button
                variant="default"
                size="sm"
                onClick={() => {
                  setDeepLinkOwnerId(undefined)
                  firstPage()
                }}
                aria-label={`Clear filter: owner ${users.find((u) => u.id === deepLinkOwnerId)?.name ?? 'selected rep'}`}
              >
                Owner: {users.find((u) => u.id === deepLinkOwnerId)?.name ?? 'selected rep'} ✕
              </Button>
            ) : null}
            <ColumnPicker
              columns={columns}
              columnVisibility={columnVisibility}
              onColumnVisibilityChange={setColumnVisibility}
            />
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">Loading accounts...</div>
          ) : accounts.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No accounts found. Create your first account to get started.
            </div>
          ) : isMobile ? (
            <MobileAccountList
              accounts={accounts}
              selectedIds={selectedIds}
              onToggleSelect={handleToggleSelect}
            />
          ) : (
            <ResizableTable
              columns={columns}
              data={accounts}
              storageKey="accounts-column-widths"
              defaultColumnSizing={accountsDefaultColumnSizing}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSort={handleSort}
              columnVisibility={columnVisibility}
              onColumnVisibilityChange={setColumnVisibility}
            />
          )}

          {/* Pagination */}
          {!isLoading && total > limit && (
            <div className="flex items-center justify-between px-2 py-4">
              <div className="text-sm text-muted-foreground">
                Showing {(currentPage - 1) * limit + 1} to {Math.min(currentPage * limit, total)} of{' '}
                {total} accounts
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={prevPage} disabled={!hasPrevPage}>
                  Previous
                </Button>
                <div className="flex items-center gap-1 text-sm">
                  Page {currentPage} of {totalPages}
                </div>
                <Button variant="outline" size="sm" onClick={nextPage} disabled={!hasNextPage}>
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <CreateAccountModal
        open={createModalOpen}
        onOpenChange={setCreateModalOpen}
        onCreated={(account) => navigate(`/accounts/${account.id}?tab=attachments`)}
      />

      <BulkDeleteDialog
        open={bulkDeleteDialogOpen}
        onOpenChange={setBulkDeleteDialogOpen}
        selectedCount={selectedIds.size}
        entityType="account"
        onConfirm={handleBulkDelete}
        isDeleting={bulkDeleteMutation.isPending}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent aria-describedby="delete-account-description">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Account</AlertDialogTitle>
            <AlertDialogDescription id="delete-account-description">
              Are you sure you want to delete the account &ldquo;{accountToDelete?.name}&rdquo;?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel aria-label="Cancel delete operation">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={deleteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              aria-label={`Confirm delete account ${accountToDelete?.name}`}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <BulkUpdateModal
        open={bulkUpdateModalOpen}
        onOpenChange={setBulkUpdateModalOpen}
        selectedCount={selectedIds.size}
        entityType="account"
        fields={accountsUpdateFields}
        onConfirm={handleBulkUpdate}
        isUpdating={bulkUpdateMutation.isPending}
      />

      <BulkAssignModal
        open={bulkAssignModalOpen}
        onOpenChange={setBulkAssignModalOpen}
        selectedCount={selectedIds.size}
        entityType="account"
        onConfirm={handleBulkAssign}
        isAssigning={bulkAssignMutation.isPending}
      />

      {selectedIds.size === 2 &&
        (() => {
          const [idA, idB] = Array.from(selectedIds) as [string, string]
          return (
            <MergeRecordsModal
              entityType="Account"
              recordIdA={idA}
              recordIdB={idB}
              open={mergeModalOpen}
              onOpenChange={(open) => {
                setMergeModalOpen(open)
                if (!open) setSelectedIds(new Set())
              }}
            />
          )
        })()}
    </div>
  )
}
